// Canonical species list shared by every "what did you catch" form on the site, so a
// catch logged on one page matches the same name (and fish icon) everywhere else. Every
// icon here is either its own dedicated render or an intentional shared fallback (Steelhead/
// Coho Salmon reuse a close relative's dedicated art) — never a crop pulled out of a shared
// multi-fish reference sheet.
export const SPECIES_OPTIONS = [
  { label: 'Largemouth Bass', icon: 'largemouth' },
  { label: 'Smallmouth Bass', icon: 'smallmouth' },
  { label: 'Striped Bass', icon: 'stripedbass' },
  { label: 'Weakfish', icon: 'weakfish' },
  { label: 'Bluegill', icon: 'bluegill' },
  { label: 'Yellow Perch', icon: 'yellowperch' },
  { label: 'Northern Pike', icon: 'pike' },
  { label: 'Chain Pickerel', icon: 'chainpickerel' },
  { label: 'Walleye', icon: 'walleye' },
  { label: 'Northern Snakehead', icon: 'snakehead' },
  { label: 'Catfish', icon: 'catfish' },
  { label: 'Carp', icon: 'carp' },
  { label: 'Brown Trout', icon: 'browntrout' },
  { label: 'Rainbow Trout', icon: 'rainbowtrout' },
  { label: 'Brook Trout', icon: 'brooktrout' },
  { label: 'Lake Trout', icon: 'laketrout' },
  { label: 'Steelhead', icon: 'trout' },
  { label: 'Atlantic Salmon', icon: 'salmon' },
  { label: 'Coho Salmon', icon: 'salmon' },
  { label: 'Tuna', icon: 'tuna' },
  { label: 'Bluefish', icon: 'bluefish' },
  { label: 'Mahi Mahi', icon: 'mahimahi' },
  { label: 'Shark', icon: 'shark' },
  { label: 'Swordfish', icon: 'swordfish' },
  { label: 'Fluke / Flounder', icon: 'flounder' },
  { label: 'Tautog', icon: 'tautog' },
  { label: 'Black Sea Bass', icon: 'blackseabass' },
  { label: 'Snook', icon: 'snook' },
];

const BY_LABEL = new Map(SPECIES_OPTIONS.map((option) => [option.label.toLowerCase(), option.icon]));

// Fuzzy fallback for a species string that isn't an exact canonical label (e.g. a custom
// species someone typed in). Ordered most-specific first so e.g. "brown trout" matches
// "browntrout" rather than the more generic "trout".
const ICON_ALIASES = [
  // The third wave's names first: 'white perch' has to beat 'perch', 'tiger trout' 'trout',
  // 'sandbar shark' 'shark', 'white marlin' 'marlin'.
  ['white perch', 'whiteperch'],
  ['fallfish', 'fallfish'],
  ['rock bass', 'rockbass'],
  ['channel cat', 'channelcatfish'],
  ['white cat', 'whitecatfish'],
  ['landlocked', 'landlockedsalmon'],
  ['splake', 'splake'],
  ['tiger trout', 'tigertrout'],
  ['pumpkinseed', 'pumpkinseed'],
  ['warmouth', 'warmouth'],
  ['florida', 'floridabass'],
  ['winter flounder', 'winterflounder'],
  ['kingfish', 'northernkingfish'],
  ['sea robin', 'searobin'],
  ['searobin', 'searobin'],
  ['toadfish', 'oystertoadfish'],
  ['american eel', 'americaneel'],
  ['spanish mackerel', 'spanishmackerel'],
  ['king mackerel', 'kingmackerel'],
  ['bonito', 'bonito'],
  ['albacore', 'littletunny'],
  ['albie', 'littletunny'],
  ['little tunny', 'littletunny'],
  ['pompano', 'pompano'],
  ['sandbar', 'sandbarshark'],
  ['yellowfin', 'yellowfintuna'],
  ['bluefin', 'bluefintuna'],
  ['bigeye', 'bigeyetuna'],
  ['thresher', 'threshershark'],
  ['mako', 'makoshark'],
  ['tilefish', 'tilefish'],
  ['white marlin', 'whitemarlin'],
  ['sailfish', 'sailfish'],
  ['opah', 'opah'],
  ['moonfish', 'opah'],
  ['seatrout', 'spottedseatrout'],
  ['speckled', 'spottedseatrout'],
  ['ladyfish', 'ladyfish'],
  ['lemon', 'lemonshark'],
  ['roosterfish', 'roosterfish'],
  ['rooster', 'roosterfish'],
  ['yellowtail', 'yellowtail'],
  ['calico', 'calicobass'],
  ['kelp bass', 'calicobass'],
  ['halibut', 'californiahalibut'],
  ['lingcod', 'lingcod'],
  ['cabezon', 'cabezon'],
  ['giant sea bass', 'giantseabass'],
  ['striped marlin', 'stripedmarlin'],
  ['corvina', 'corvina'],
  ['sierra', 'sierra'],
  ['largemouth', 'largemouth'],
  ['smallmouth', 'smallmouth'],
  ['striped bass', 'stripedbass'],
  ['stripedbass', 'stripedbass'],
  ['striper', 'stripedbass'],
  ['weakfish', 'weakfish'],
  ['weak', 'weakfish'],
  ['bluegill', 'bluegill'],
  ['yellow perch', 'yellowperch'],
  ['yellowperch', 'yellowperch'],
  ['perch', 'yellowperch'],
  ['chain pickerel', 'chainpickerel'],
  ['chainpickerel', 'chainpickerel'],
  ['pickerel', 'chainpickerel'],
  ['walleye', 'walleye'],
  ['snakehead', 'snakehead'],
  ['catfish', 'catfish'],
  ['carp', 'carp'],
  ['brown trout', 'browntrout'],
  ['browntrout', 'browntrout'],
  ['rainbow trout', 'rainbowtrout'],
  ['rainbowtrout', 'rainbowtrout'],
  ['lake trout', 'laketrout'],
  ['laketrout', 'laketrout'],
  ['brook trout', 'brooktrout'],
  ['brooktrout', 'brooktrout'],
  ['steelhead', 'trout'],
  ['trout', 'trout'],
  ['salmon', 'salmon'],
  ['bluefish', 'bluefish'],
  ['mahi', 'mahimahi'],
  ['shark', 'shark'],
  ['flounder', 'flounder'],
  ['fluke', 'flounder'],
  ['tautog', 'tautog'],
  ['blackfish', 'tautog'],
  ['black sea bass', 'blackseabass'],
  ['blackseabass', 'blackseabass'],
  ['sea bass', 'blackseabass'],
  ['snook', 'snook'],
  ['muskellunge', 'muskie'],
  ['muskie', 'muskie'],
  ['musky', 'muskie'],
  ['shad', 'americanshad'],
  ['arctic char', 'arcticchar'],
  ['char', 'arcticchar'],
  ['crappie', 'crappie'],
  ['bowfin', 'bowfin'],
  ['alligator gar', 'alligatorgar'],
  ['gar', 'longnosegar'],
  ['porgy', 'porgy'],
  ['scup', 'porgy'],
  ['black drum', 'blackdrum'],
  ['cobia', 'cobia'],
  ['wahoo', 'wahoo'],
  ['marlin', 'bluemarlin'],
  ['bonefish', 'bonefish'],
  ['permit', 'permit'],
  ['tarpon', 'tarpon'],
  ['redfish', 'redfish'],
  ['red drum', 'redfish'],
  ['barracuda', 'barracuda'],
  ['crevalle', 'jackcrevalle'],
  ['jack', 'jackcrevalle'],
  ['snapper', 'mangrovesnapper'],
  ['pike', 'pike'],
  ['tuna', 'tuna'],
  ['bass', 'largemouth'],
];

export default function speciesIcon(species) {
  const key = (species || '').toLowerCase();
  const exact = BY_LABEL.get(key);
  if (exact) return exact;
  const alias = ICON_ALIASES.find(([keyword]) => key.includes(keyword));
  return alias ? alias[1] : 'largemouth';
}
